import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client";
import {
  GET_AUDIT_CONTROL_CHECK_DATA,
  GET_AUDIT_FRAMEWORK_MIDDLE_PANEL,
} from "../../../../apis/settingsApis/audit-apis/auditApis";
import { GET_CHECK_DETAILS_BY_TYPE } from "../../../../apis/settingsApis/framework-apis/frameworkApis";
import { useAuditContext } from "../../../../hooks/useAuditContext";
import { CheckCategory, CheckStatus } from "../../../../utils/enums";

const isPolicyCheck = (check) =>
  (check?.activeCheckCategory || "")
    .toLowerCase()
    .split(",")
    .some((part) => part.trim() === CheckCategory.POLICY_CHECK);

// A pending check never ran, so it has no resources or evidence to show.
// Policy checks are always shown.
const hasEvidence = (check) =>
  isPolicyCheck(check) || check?.checkStatus !== CheckStatus.PENDING;

const checkKey = (check) => check?.checkType || check?.activeCheckName;

export const useControlDetails = (audit, controlId, auditIdFromParams) => {
  const [control, setControl] = useState(null);
  const [selectedCheckType, setSelectedCheckType] = useState(null);

  const auditId = audit?.id || auditIdFromParams;

  const { auditEndDate } = useAuditContext({
    auditId,
  });

  // Fetch audit framework data (contains all control info including auditControlId, statuses, checks)
  const { data: auditFrameworkData, loading: frameworkLoading } = useQuery(
    GET_AUDIT_FRAMEWORK_MIDDLE_PANEL,
    {
      variables: {
        audit_id: audit?.id || auditIdFromParams,
      },
      skip: !(audit?.id || auditIdFromParams),
      fetchPolicy: "cache-and-network",
    }
  );

  // Which checks have data to show; the export leaves out the same checks
  const { data: checkDataResult, loading: checkDataLoading } = useQuery(
    GET_AUDIT_CONTROL_CHECK_DATA,
    {
      variables: {
        audit_id: auditId,
        control_id: parseInt(controlId),
      },
      skip: !auditId || !controlId,
      fetchPolicy: "cache-and-network",
    }
  );

  // Fetch selected check details
  const {
    data: checkData,
    loading: checkLoading,
    refetch: refetchCheckDetails,
  } = useQuery(GET_CHECK_DETAILS_BY_TYPE, {
    variables: {
      check_type: selectedCheckType,
      audit_id: auditId,
      end_time: auditEndDate,
    },
    skip: !selectedCheckType,
    fetchPolicy: "network-only",
  });

  const checkDetails = checkData?.getCheckDetailsByType;
  const activeCheckId = checkDetails?.active_check_id
    ? parseInt(checkDetails.active_check_id)
    : null;

  // Extract control from audit framework data
  useEffect(() => {
    if (!auditFrameworkData?.getAuditFrameworkMiddlePanel || !controlId) return;

    let foundControl = null;

    auditFrameworkData.getAuditFrameworkMiddlePanel.areaDetails?.forEach((area) => {
      if (!area) return;
      area?.criterias?.forEach((criteria) => {
        if (!criteria) return;
        criteria?.controls?.forEach((ctrl) => {
          if (!ctrl || !ctrl.controlsId) return;
          if (ctrl.controlsId === parseInt(controlId)) {
            foundControl = {
              ...ctrl,
              controlsDomain: area.area_name,
            };
          }
        });
      });
    });

    if (foundControl) {
      setControl(foundControl);
    }
  }, [auditFrameworkData, controlId]);

  const checksWithoutData = useMemo(() => {
    const ids = (checkDataResult?.getAuditControlCheckData || [])
      .filter((row) => row && row.hasData === false)
      .map((row) => String(row.activeCheckId));
    return new Set(ids);
  }, [checkDataResult]);

  // Sort checks by name, leaving out checks that have no evidence or data
  const sortedChecks = useMemo(() => {
    if (!control?.checks) return [];
    return control.checks
      .filter(
        (check) =>
          hasEvidence(check) &&
          !checksWithoutData.has(String(check.activeCheckId))
      )
      .sort((a, b) => {
      const nameA = (a?.activeCheckName || a?.checkName || "").toLowerCase();
      const nameB = (b?.activeCheckName || b?.checkName || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [control?.checks, checksWithoutData]);

  const checkDataReady = !!checkDataResult || (!checkDataLoading && !!control);

  // Select the first visible check, and move off a check once it is hidden
  useEffect(() => {
    if (!checkDataReady) return;
    const isVisible = sortedChecks.some(
      (check) => checkKey(check) === selectedCheckType
    );
    if (isVisible) return;
    setSelectedCheckType(sortedChecks.length > 0 ? checkKey(sortedChecks[0]) : null);
  }, [checkDataReady, sortedChecks, selectedCheckType]);

  const checkCategoryLower = checkDetails?.active_check_category?.toLowerCase() || "";
  const checkCategory = useMemo(() => {
    if (!checkCategoryLower) return null;
    if (checkCategoryLower.includes("pass_with_evidence")) return "pass_with_evidence";
    if (checkCategoryLower.includes("azure")) return "azure_system_check";
    if (checkCategoryLower.includes("aws")) return "aws_system_check";
    if (checkCategoryLower.includes("gcp")) return "gcp_system_check";
    if (checkCategoryLower.includes("people")) return "people_check";
    return null;
  }, [checkCategoryLower]);

  // Every check on the control is hidden, so there is nothing to review
  const needsManualEvidence =
    (control?.checks?.length || 0) > 0 && sortedChecks.length === 0;

  const isLoading = frameworkLoading || !control || !checkDataReady;

  return {
    control,
    sortedChecks,
    selectedCheckType,
    setSelectedCheckType,
    checkDetails,
    activeCheckId,
    checkCategory,
    needsManualEvidence,
    isLoading,
    checkLoading,
    refetchCheckDetails,
  };
};
